import React, { useState } from 'react';
import styles from '../styles/joinPreferencesModal.module.css';
import { useLocale } from '../context/LocaleContext';

export interface JoinPreferences {
  display_preference: string;
  include_on_printed_phone_list: boolean;
  email_notifications_enabled: boolean;
  sponsor_willingness: string;
}

const DEFAULT_PREFERENCES: JoinPreferences = {
  display_preference: 'name_only',
  include_on_printed_phone_list: false,
  email_notifications_enabled: true,
  sponsor_willingness: 'no',
};

const DISPLAY_OPTIONS = [
  { value: 'name_only', labelKey: 'meetings.join_prefs.display.name_only' },
  { value: 'name_and_location', labelKey: 'meetings.join_prefs.display.name_and_location' },
  { value: 'name_and_phone', labelKey: 'meetings.join_prefs.display.name_and_phone' },
  { value: 'hidden', labelKey: 'meetings.join_prefs.display.hidden' },
];

const SPONSOR_OPTIONS = [
  { value: 'no', labelKey: 'dialog.no' },
  { value: 'yes', labelKey: 'dialog.yes' },
  { value: 'temporary', labelKey: 'meetings.join_prefs.sponsor.temporary' },
];

interface JoinPreferencesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (preferences: JoinPreferences) => void;
  isSubmitting?: boolean;
  meetingName?: string;
}

const JoinPreferencesModal: React.FC<JoinPreferencesModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
  meetingName,
}) => {
  const { t } = useLocale();
  const [display_preference, setDisplayPreference] = useState(DEFAULT_PREFERENCES.display_preference);
  const [include_on_printed_phone_list, setIncludeOnPrintedPhoneList] = useState(DEFAULT_PREFERENCES.include_on_printed_phone_list);
  const [email_notifications_enabled, setEmailNotificationsEnabled] = useState(DEFAULT_PREFERENCES.email_notifications_enabled);
  const [sponsor_willingness, setSponsorWillingness] = useState(DEFAULT_PREFERENCES.sponsor_willingness);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      display_preference,
      include_on_printed_phone_list,
      email_notifications_enabled,
      sponsor_willingness,
    });
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 className={styles.title}>{t('meetings.join_prefs.title')}</h2>
          <button type="button" className={styles.closeBtn} onClick={onClose} aria-label={t('dialog.close')}>
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.body}>
            {meetingName && (
              <p className={styles.meetingName}>{meetingName}</p>
            )}
            <label className={styles.label}>
              {t('meetings.join_prefs.display_label')}
            </label>
            <select
              value={display_preference}
              onChange={(e) => setDisplayPreference(e.target.value)}
              className={styles.select}
            >
              {DISPLAY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{t(o.labelKey)}</option>
              ))}
            </select>

            <label className={styles.checkLabel}>
              <input
                type="checkbox"
                checked={include_on_printed_phone_list}
                onChange={(e) => setIncludeOnPrintedPhoneList(e.target.checked)}
              />
              <span>{t('meetings.join_prefs.include_phone_list')}</span>
            </label>

            <label className={styles.checkLabel}>
              <input
                type="checkbox"
                checked={email_notifications_enabled}
                onChange={(e) => setEmailNotificationsEnabled(e.target.checked)}
              />
              <span>{t('meetings.join_prefs.email_notifications')}</span>
            </label>

            <label className={styles.label}>
              {t('meetings.join_prefs.sponsor_label')}
            </label>
            <select
              value={sponsor_willingness}
              onChange={(e) => setSponsorWillingness(e.target.value)}
              className={styles.select}
            >
              {SPONSOR_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{t(o.labelKey)}</option>
              ))}
            </select>

            <p className={styles.privacyNote}>
              {t('meetings.join_prefs.privacy_note')}
            </p>
          </div>
          <div className={styles.footer}>
            <button type="button" className={styles.cancelBtn} onClick={onClose}>
              {t('dialog.cancel')}
            </button>
            <button type="submit" className={styles.primaryBtn} disabled={isSubmitting}>
              {isSubmitting ? t('meetings.join_prefs.adding') : t('meetings.join_prefs.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default JoinPreferencesModal;
export { DEFAULT_PREFERENCES };
